import supabase from '../supabase/supabase';
import { reportService } from './reportService';

const POSTED_STATUSES = [
  'Posted',
  'posted',
  'Cleared',
  'cleared',
  'Reconciled',
  'reconciled',
  'AUTO_POSTED',
  'auto_posted'
];

const normalizeType = (value) => String(value || '').trim().toLowerCase();

const getSection = (account) => {
  const type = normalizeType(account.type);
  const subType = normalizeType(account.sub_type);

  if (type === 'revenue' || type === 'income') return 'income';

  if (
    type === 'cost of goods sold' ||
    type === 'cost of sales' ||
    type === 'cogs' ||
    subType === 'cost of goods sold' ||
    subType === 'cost of sales' ||
    subType === 'cogs'
  ) {
    return 'costOfGoodsSold';
  }

  if (type === 'expense' || type === 'expenses') return 'expenses';

  return null;
};

const roundCurrency = (value) => Number(Number(value || 0).toFixed(2));

const createDetail = (account) => ({
  id: account.id,
  name: account.name,
  code: account.code || '',
  amount: 0,
  transactions: []
});

export const profitLossService = {
  async generate(startDate, endDate, accountId, basis = 'accrual') {
    const { account_id, coa } = await reportService.getBaseData(accountId);

    let transactionQuery = supabase
      .schema('accounting')
      .from('transactions_20240522')
      .select('id, coa_id, date, description, debit, credit, reference_id, reference_type, status, payee, created_at')
      .eq('account_id', account_id)
      .gte('date', startDate)
      .lte('date', endDate)
      .in('status', POSTED_STATUSES)
      .order('date', { ascending: false })
      .order('created_at', { ascending: false });

    if (basis === 'cash') {
      transactionQuery = transactionQuery.in('status', [
        'Posted',
        'posted',
        'Cleared',
        'cleared',
        'Reconciled',
        'reconciled',
        'AUTO_POSTED',
        'auto_posted'
      ]);
    }

    const { data: transactions, error } = await transactionQuery;

    if (error) throw error;

    const accountsById = new Map(
      (coa || [])
        .filter((account) => account.is_active !== false)
        .map((account) => [account.id, account])
    );

    const detailsById = new Map();

    accountsById.forEach((account) => {
      const section = getSection(account);

      if (section) {
        detailsById.set(account.id, {
          section,
          detail: createDetail(account)
        });
      }
    });

    const report = {
      income: { total: 0, accounts: {} },
      costOfGoodsSold: { total: 0, accounts: {} },
      expenses: { total: 0, accounts: {} },
      grossProfit: 0,
      netIncome: 0,
      accountDetails: {
        income: [],
        costOfGoodsSold: [],
        expenses: []
      },
      metadata: {
        accountId: account_id,
        startDate,
        endDate,
        basis,
        transactionCount: transactions?.length || 0,
        source: 'accounting.transactions_20240522'
      }
    };

    (transactions || []).forEach((transaction) => {
      const accountEntry = detailsById.get(transaction.coa_id);

      if (!accountEntry) return;

      const debit = Number(transaction.debit || 0);
      const credit = Number(transaction.credit || 0);
      const amount = accountEntry.section === 'income'
        ? credit - debit
        : debit - credit;

      accountEntry.detail.amount += amount;
      accountEntry.detail.transactions.push({
        id: transaction.id,
        date: transaction.date,
        description: transaction.description || '',
        reference: transaction.reference_id || transaction.reference_type || '',
        debit,
        credit,
        amount,
        status: transaction.status || '',
        payee: transaction.payee || ''
      });

      report[accountEntry.section].total += amount;
    });

    detailsById.forEach(({ section, detail }) => {
      detail.amount = roundCurrency(detail.amount);
      detail.transactions.sort((first, second) => (
        `${second.date}-${second.id}`.localeCompare(`${first.date}-${first.id}`)
      ));

      report.accountDetails[section].push(detail);

      if (detail.amount !== 0) {
        report[section].accounts[detail.name] = detail.amount;
      }
    });

    Object.values(report.accountDetails).forEach((accounts) => {
      accounts.sort((first, second) => (
        first.name.localeCompare(second.name)
      ));
    });

    report.income.total = roundCurrency(report.income.total);
    report.costOfGoodsSold.total = roundCurrency(report.costOfGoodsSold.total);
    report.expenses.total = roundCurrency(report.expenses.total);
    report.grossProfit = roundCurrency(
      report.income.total - report.costOfGoodsSold.total
    );
    report.netIncome = roundCurrency(
      report.grossProfit - report.expenses.total
    );

    return report;
  }
};